/* eslint-disable max-lines */
import { useCallback } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import type {
  ActivityMessage,
  ChatAppResponse,
  ChatSessionModel,
  ErrorMessage,
  ServerMessage,
} from "@/api/models"
import type { ChatSlice } from "@/stores/slices/chat-slice"
import type { StoreApi, UseBoundStore } from "zustand"

import { routeFairUseError, routeFairUseMetadata } from "@/features/fair-use/fair-use-ws-bridge"
import { logger } from "@/lib/logger"
import { queryKeys } from "@/lib/query-keys"
import { translateActivityMessage } from "@/utils/activity-message-translator"

import {
  chatRequestOwnsSession,
  isChatRequestGenerationCurrent,
  isChatRequestSessionDisposalGenerationCurrent,
  isChatRequestSessionDisposed,
} from "./chat-request-registry"
import { useChatSessionSyncPublisher } from "./use-chat-session-cross-tab-sync"
import {
  activitySideEffectKey,
  applyActivitySideEffects,
  getActivityDocumentReferences,
  mergeDocumentReferences,
  mergeIncomingActivity,
  mergeSavedResponseContext,
} from "./use-websocket-message-handler-utils"
import { asSteerServerFrame } from "../types/steer-types"
import { mergeActivitiesPreservingClientInjected } from "../utils/merge-client-injected-activities"

interface UseWebSocketMessageHandlerProps {
  useChatStore: () => UseBoundStore<StoreApi<ChatSlice>>
  setActivityMessage: (message: string) => void
  setIsLoading: (loading: boolean) => void
  setHasError: (hasError: boolean) => void
  setErrorMessage: (message: string) => void
  sessionType?: string
  /** Cursor-style message queue (#2437) gate: the undelivered-steer fallback
   *  below only requeues when the queue actually auto-drains — otherwise the
   *  note would sit in `queuedMessages` forever (drain requires this flag). */
  isMessageQueueEnabled?: boolean
}

type MessageContext = {
  sessionId?: string | null
  isActive?: boolean
  requestToken?: symbol
  requestGenerationId?: string
  disposalGeneration?: number
}

const getMessageId = (response: ChatAppResponse): string | undefined =>
  response.messageId ?? response.context.message_id

export function useWebSocketMessageHandler({
  useChatStore,
  setActivityMessage,
  setIsLoading,
  setHasError,
  setErrorMessage,
  sessionType,
  isMessageQueueEnabled,
}: UseWebSocketMessageHandlerProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const chatStore = useChatStore()
  // Cross-window sync (issue #2548). `sessionType` is optional on this hook;
  // an empty scope publishes nothing, which matches the `done` branch's own
  // `if (sessionType && ...)` guard on the equivalent local invalidate.
  const publishSessionChange = useChatSessionSyncPublisher({ sessionType: sessionType ?? "" })

  const messageHandler = useCallback(
    (message: ServerMessage, context?: MessageContext) => {
      const sessionIdFromContext = context?.sessionId ?? null
      const effectiveSessionId =
        sessionIdFromContext ?? chatStore.getState().currentSessionId ?? null
      const frameSessionId =
        message.type === "message_saved" || message.type === "title_state_changed"
          ? (message.data.session_id ?? effectiveSessionId)
          : effectiveSessionId
      const frameGenerationId = (message as unknown as { _request_generation_id?: string })
        ._request_generation_id

      // Fair-use budget data is per-USER, not per-session (spec section 5a) —
      // route it BEFORE the disposed-session guard below. That guard exists
      // for session-scoped UI state (messages, streaming flags, history
      // invalidation); routing here first means a turn whose session was
      // deleted mid-stream still updates the composer's usage indicator, and
      // a `fair_use_blocked` error still auto-opens the request-more popover,
      // instead of the frame being silently dropped and the user seeing no
      // signal that they were just blocked. Both route* calls are safe to
      // call unconditionally — they no-op on a payload with no fair-use data
      // — so the branches further down that also call them for a NOT-disposed
      // session simply reuse fairUseErrorCode instead of recomputing it.
      let fairUseErrorCode: ReturnType<typeof routeFairUseError> = null
      if (message.type === "done") {
        routeFairUseMetadata(queryClient, message.data)
      } else if (message.type === "error") {
        fairUseErrorCode = routeFairUseError(queryClient, message as ErrorMessage)
      }

      if (
        frameSessionId &&
        (isChatRequestSessionDisposed(chatStore, frameSessionId) ||
          (context?.requestToken &&
            !chatRequestOwnsSession(chatStore, frameSessionId, context.requestToken)) ||
          (context?.disposalGeneration !== undefined &&
            !isChatRequestSessionDisposalGenerationCurrent(
              chatStore,
              frameSessionId,
              context.disposalGeneration
            )) ||
          (context?.requestGenerationId !== undefined &&
            (frameGenerationId !== context.requestGenerationId ||
              !isChatRequestGenerationCurrent(
                chatStore,
                frameSessionId,
                context.requestGenerationId
              ))))
      )
        return

      // Steer/cancel control frames (issue #2438): direct WS frames outside
      // the ServerMessage union, narrowed defensively before the typed
      // branches below. They only arrive on the Explorer main-chat WS with
      // the explorer_steer flag on — a no-op everywhere else.
      const steerFrame = asSteerServerFrame(message)
      if (steerFrame) {
        if (!effectiveSessionId) return
        const store = chatStore.getState()
        if (steerFrame.type === "steer_ready") {
          store.updateSession(effectiveSessionId, { steerReady: true })
        } else if (steerFrame.type === "cancel_ack") {
          store.updateSession(effectiveSessionId, { cancelAckReceived: true })
        } else {
          const { steer_id: steerId, status } = steerFrame.data
          const steerNotes =
            effectiveSessionId in store.sessionStates
              ? store.sessionStates[effectiveSessionId].steerNotes
              : []
          const noteIndex = steerNotes.findIndex((note) => note.steer_id === steerId)
          // A status for an unknown note (turn already wound down, other tab)
          // has nothing to settle — drop it.
          if (noteIndex < 0) return
          if (status === "undelivered" && isMessageQueueEnabled) {
            // The steer never reached the model (e.g. a single-iteration/
            // no-boundary turn that never hit a tool-loop checkpoint) — an
            // orphaned chip would just die silently. Fall back to the queue
            // instead so the note still gets sent, on the next turn. Gated on
            // the queue feature: composer-based steering (this fallback's
            // main trigger) only happens when the queue is unavailable, and
            // without auto-drain a requeued note would sit in `queuedMessages`
            // forever instead of the terminal "undelivered" chip it replaces.
            const note = steerNotes[noteIndex]
            const remainingNotes = steerNotes.filter((_, i) => i !== noteIndex)
            const queuedMessages =
              effectiveSessionId in store.sessionStates
                ? store.sessionStates[effectiveSessionId].queuedMessages
                : []
            store.updateSession(effectiveSessionId, {
              steerNotes: remainingNotes,
              queuedMessages: [...queuedMessages, { id: crypto.randomUUID(), text: note.content }],
            })
            return
          }
          if (status === "delivered") {
            // Issue #2469: confirmation moves to the feed + Activity History
            // immediately — remove the composer chip, attach a Steered marker
            // to the live turn index, and inject a synthetic activity row.
            // `answersForSpreadsheet` appends `streamingAnswer` at
            // `messages.length`, so that index is the live slot while streaming.
            const note = steerNotes[noteIndex]
            const remainingNotes = steerNotes.filter((_, i) => i !== noteIndex)
            const sessionState =
              effectiveSessionId in store.sessionStates
                ? store.sessionStates[effectiveSessionId]
                : undefined
            if (!sessionState) {
              store.updateSession(effectiveSessionId, { steerNotes: remainingNotes })
              return
            }
            const isLiveTurn = Boolean(
              sessionState.streamingAnswer || sessionState.isStreaming || sessionState.isLoading
            )
            let liveIndex = -1
            if (isLiveTurn) {
              // `answersForSpreadsheet` appends streamingAnswer at this index.
              liveIndex = sessionState.messages.length
            } else if (sessionState.messages.length > 0) {
              liveIndex = sessionState.messages.length - 1
            }
            // Backend activity timestamps are Unix seconds (time.time()).
            // Date.now() is ms — using ms here sorted steer_applied after every
            // later server activity, so the live header stuck on "Steered…"
            // and flickered whenever a new row briefly landed last.
            const steerActivity: ActivityMessage = {
              type: "activity",
              activity_type: "steer_applied",
              message: `Steered: ${note.content}`,
              timestamp: Math.floor(Date.now() / 1000),
            }
            const nextMarkers =
              liveIndex >= 0
                ? {
                    messageSteerMarkers: {
                      ...sessionState.messageSteerMarkers,
                      [liveIndex]: [
                        ...(sessionState.messageSteerMarkers[liveIndex] ?? []),
                        note.content,
                      ],
                    },
                  }
                : {
                    // No live index yet — settle-time drain still attaches these.
                    pendingSteerMarkerQueue: [
                      ...sessionState.pendingSteerMarkerQueue,
                      note.content,
                    ],
                  }
            let streamingAnswer = sessionState.streamingAnswer
            let messages = sessionState.messages
            if (streamingAnswer) {
              const [question, response] = streamingAnswer
              streamingAnswer = [
                question,
                {
                  ...response,
                  activities: [...(response.activities ?? []), steerActivity],
                },
              ]
            } else if (liveIndex >= 0 && liveIndex < messages.length) {
              const nextMessages = [...messages]
              const [question, response] = nextMessages[liveIndex]
              nextMessages[liveIndex] = [
                question,
                {
                  ...response,
                  activities: [...(response.activities ?? []), steerActivity],
                },
              ]
              messages = nextMessages
            }
            store.updateSession(effectiveSessionId, {
              steerNotes: remainingNotes,
              streamingAnswer,
              messages,
              ...nextMarkers,
            })
            return
          }
          const updatedNotes = [...steerNotes]
          updatedNotes[noteIndex] = { ...updatedNotes[noteIndex], status }
          store.updateSession(effectiveSessionId, {
            steerNotes: updatedNotes,
          })
        }
        return
      }

      if (message.type === "activity") {
        const activityMsg = message as ActivityMessage
        const sessionId = effectiveSessionId
        if (!sessionId) return

        const store = chatStore.getState()
        if (!(sessionId in store.sessionStates)) {
          store.initSession(sessionId)
        }

        const sessionState = chatStore.getState().sessionStates[sessionId]
        const currentStreaming = sessionState.streamingAnswer
        const isActiveSession = sessionId === store.currentSessionId
        const translatedActivity = translateActivityMessage(activityMsg, t)

        if (!currentStreaming) {
          // Keep queue state and loading text updated even before the first stream chunk arrives.
          if (activityMsg.activity_type === "queue_status" && activityMsg.details?.request_id) {
            store.updateSession(sessionId, {
              queueRequestId: activityMsg.details.request_id as string,
            })
          }

          if (isActiveSession) {
            setActivityMessage(translatedActivity)
          }
          return
        }

        const [question, response] = currentStreaming

        // Late activity frames from the previous turn can arrive after the
        // message queue (or a fast follow-up) has already seeded the next
        // streamingAnswer. Those frames carry older timestamps — drop them
        // so Activity History doesn't briefly show the prior turn's count.
        // Backend activity timestamps are Unix seconds (time.time());
        // stream_started_at is Date.now() milliseconds — normalize before compare.
        if (
          typeof response.stream_started_at === "number" &&
          typeof activityMsg.timestamp === "number" &&
          activityMsg.timestamp * 1000 < response.stream_started_at
        ) {
          if (isActiveSession) {
            setActivityMessage(translatedActivity)
          }
          return
        }

        const existingActivities = response.activities || []
        const activityTranslationKey = activityMsg.details?.translation_key
        const activityKey = activitySideEffectKey(activityMsg)
        const incomingDocumentReferences = getActivityDocumentReferences(activityMsg)
        // Canvas refresh + query invalidations — shared with the durable
        // hydration path so both transports behave the same (#2961).
        applyActivitySideEffects(activityMsg, queryClient)

        let updatedResponse = response
        const mergedActivities = mergeIncomingActivity({
          existingActivities,
          activityMsg,
          activityTranslationKey,
          activityKey,
        })
        if (mergedActivities !== existingActivities) {
          updatedResponse = {
            ...updatedResponse,
            activities: mergedActivities,
          }
        }

        if (incomingDocumentReferences.length > 0) {
          const mergedReferences = mergeDocumentReferences(
            updatedResponse.context.document_references,
            incomingDocumentReferences
          )
          updatedResponse = {
            ...updatedResponse,
            context: {
              ...updatedResponse.context,
              document_references: mergedReferences,
            },
          }
        }

        if (updatedResponse !== response) {
          type SessionStateUpdate = {
            streamingAnswer: [string, ChatAppResponse]
            queueRequestId?: string
          }

          const updatePayload: SessionStateUpdate = {
            streamingAnswer: [question, updatedResponse],
          }

          if (activityMsg.activity_type === "queue_status" && activityMsg.details?.request_id) {
            updatePayload.queueRequestId = activityMsg.details.request_id as string
          }

          store.updateSession(sessionId, updatePayload)
        }

        if (isActiveSession) {
          setActivityMessage(translatedActivity)
        }
      } else if (message.type === "message_saved") {
        const { session_id: sessionIdFromMessage, question, response } = message.data
        const targetSessionId = sessionIdFromMessage ?? effectiveSessionId
        const normalizedQuestion = question ?? ""
        const messageId = getMessageId(response)

        if (!targetSessionId) {
          return
        }
        if (!messageId) {
          logger.error("message_saved without message_id; skipping response")
          return
        }

        const store = chatStore.getState()
        if (!(targetSessionId in store.sessionStates)) {
          store.initSession(targetSessionId)
        }
        const sessionState = chatStore.getState().sessionStates[targetSessionId]

        if (
          sessionState.streamingAnswer &&
          sessionState.streamingAnswer[0] === normalizedQuestion
        ) {
          const [, streamAnswer] = sessionState.streamingAnswer

          // If the stream handler already marked this as completed, its settle
          // timeout will handle the streamingAnswer → messages move. Just merge
          // backend metadata (messageId, files) into the streaming answer so the
          // settle timeout picks them up, avoiding a double-finalization flicker.
          if (streamAnswer.completed) {
            store.updateSession(targetSessionId, {
              isAwaitingSave: false,
              // Real save ack (vs. the save-guard watchdog releasing a stuck
              // guard) — stop-and-redirect only auto-sends after this.
              sawMessageSaved: true,
              streamingAnswer: [
                sessionState.streamingAnswer[0],
                {
                  ...streamAnswer,
                  messageId: response.messageId ?? streamAnswer.messageId,
                  assistant_files: response.assistant_files ?? streamAnswer.assistant_files,
                  user_files: response.user_files ?? streamAnswer.user_files,
                  // A cancelled turn's save carries the terminal interruption
                  // marker (issue #2438) — the settle timeout must not lose it.
                  interrupted: response.interrupted ?? streamAnswer.interrupted,
                  termination_reason:
                    response.termination_reason ?? streamAnswer.termination_reason,
                  // Prism "not captured" notice on the save ack (#3339).
                  memory_capture_status:
                    response.memory_capture_status ?? streamAnswer.memory_capture_status,
                  // Preserve the in-stream `thinking` accumulated via
                  // `deep_planning_chunk` — the backend's persisted thinking
                  // should match, but we explicitly fall back to avoid losing
                  // it on non-Deep approaches that don't round-trip the field.
                  thinking: response.thinking ?? streamAnswer.thinking,
                  context: mergeSavedResponseContext(streamAnswer.context, response.context),
                },
              ],
            })

            return
          }

          const [q] = sessionState.streamingAnswer
          // Prefer a non-empty saved activities list when the backend round-trips
          // one; otherwise keep the in-stream list. Always re-attach client-
          // injected steer_applied rows the save payload never includes.
          const savedActivities = response.activities
          const baseActivities =
            savedActivities && savedActivities.length > 0
              ? savedActivities
              : (streamAnswer.activities ?? [])
          const finalAnswer: ChatAppResponse = {
            ...streamAnswer, // Keep activities from streaming (then override below)
            content: response.content, // Use backend's complete content
            messageId: response.messageId,
            completed: true,
            // Terminal interruption marker from a cancelled turn (issue #2438).
            interrupted: response.interrupted,
            termination_reason: response.termination_reason,
            // Prism "not captured" notice on the save ack (#3339).
            memory_capture_status: response.memory_capture_status,
            assistant_files: response.assistant_files, // Files created by code interpreter
            user_files: response.user_files,
            // Prefer backend's persisted thinking (Deep Explorer's pre-approval
            // planning), but fall back to whatever we accumulated in-stream so
            // the Thinking panel doesn't disappear when the backend response
            // lacks a `thinking` field (non-Deep approaches).
            thinking: response.thinking ?? streamAnswer.thinking,
            activities: mergeActivitiesPreservingClientInjected(
              baseActivities,
              streamAnswer.activities
            ),
            context: {
              ...response.context,
              document_references:
                streamAnswer.context.document_references ?? response.context.document_references,
              tool_approvals:
                streamAnswer.context.tool_approvals ?? response.context.tool_approvals,
            },
          }

          const updatedMessages: [string, ChatAppResponse][] = [
            ...sessionState.messages,
            [q, finalAnswer],
          ]
          store.updateSession(targetSessionId, {
            messages: updatedMessages,
            streamingAnswer: null,
            isStreaming: false,
            isLoading: false,
            isAwaitingSave: false,
            sawMessageSaved: true,
            canContinue: false,
            queueRequestId: null,
            // The terminal save owns this turn, so discard any approval overlay
            // from its retired live slot if a queued stream update is dropped.
            toolApprovals: [],
            toolApprovalSessionId: null,
          })
        } else {
          const existingIndex = sessionState.messages.findIndex(
            ([, msg]) => getMessageId(msg) === messageId
          )
          if (existingIndex < 0) {
            // Fallback: the settle timeout in use-stream-handler may have moved
            // streamingAnswer → messages without a messageId (streaming doesn't
            // provide one). Match by question text to merge backend metadata
            // (messageId, assistant_files, user_files) instead of adding a duplicate.
            const fallbackIndex = sessionState.messages.findLastIndex(
              ([q, msg]) => q === normalizedQuestion && !getMessageId(msg)
            )

            if (fallbackIndex >= 0) {
              const [existingQ, existingMsg] = sessionState.messages[fallbackIndex]
              const updatedMessages = [...sessionState.messages]
              updatedMessages[fallbackIndex] = [
                existingQ,
                {
                  ...existingMsg,
                  messageId: response.messageId ?? existingMsg.messageId,
                  assistant_files: response.assistant_files ?? existingMsg.assistant_files,
                  user_files: response.user_files ?? existingMsg.user_files,
                  interrupted: response.interrupted ?? existingMsg.interrupted,
                  termination_reason: response.termination_reason ?? existingMsg.termination_reason,
                  memory_capture_status:
                    response.memory_capture_status ?? existingMsg.memory_capture_status,
                  context: mergeSavedResponseContext(existingMsg.context, response.context),
                },
              ]
              store.updateSession(targetSessionId, {
                messages: updatedMessages,
                isAwaitingSave: false,
                sawMessageSaved: true,
              })
              return
            }

            const updatedMessages: [string, ChatAppResponse][] = [
              ...sessionState.messages,
              [normalizedQuestion, response],
            ]

            // Only clear streaming flags if no active stream is in progress.
            const hasActiveStream = sessionState.streamingAnswer !== null
            store.updateSession(targetSessionId, {
              messages: updatedMessages,
              ...(hasActiveStream
                ? {}
                : {
                    streamingAnswer: null,
                    isStreaming: false,
                    isLoading: false,
                    isAwaitingSave: false,
                    sawMessageSaved: true,
                    canContinue: false,
                    queueRequestId: null,
                  }),
            })
            return
          }

          const [existingQ, existingMsg] = sessionState.messages[existingIndex]
          if (!existingMsg.completed) {
            const updatedMessages = [...sessionState.messages]
            updatedMessages[existingIndex] = [
              normalizedQuestion,
              {
                ...response,
                activities: mergeActivitiesPreservingClientInjected(
                  response.activities ?? [],
                  existingMsg.activities
                ),
              },
            ]
            store.updateSession(targetSessionId, {
              messages: updatedMessages,
              isAwaitingSave: false,
              sawMessageSaved: true,
            })
            return
          }

          // Stream already finalized this message — only merge backend metadata
          // (messageId, files) without replacing content to avoid a redundant re-render.
          const savedDocumentCount = response.context.documents?.length ?? 0
          const existingDocumentCount = existingMsg.context.documents?.length ?? 0
          // Refresh from save when the list grew, or when counts match but the save still
          // carries documents (metadata/extra_data can change without count changing).
          const needsContextFromSave =
            savedDocumentCount > existingDocumentCount ||
            (savedDocumentCount > 0 && savedDocumentCount === existingDocumentCount)

          const needsMetadata =
            (!existingMsg.messageId && response.messageId) ||
            (!existingMsg.assistant_files?.length && response.assistant_files?.length) ||
            (!existingMsg.user_files?.length && response.user_files?.length) ||
            (!existingMsg.interrupted && response.interrupted)

          if (!needsMetadata && !needsContextFromSave) {
            // Nothing to merge, but still release the save-guard so the send button
            // re-enables once the backend has confirmed the message is persisted.
            store.updateSession(targetSessionId, { isAwaitingSave: false, sawMessageSaved: true })
            return
          }

          const updatedMessages = [...sessionState.messages]
          updatedMessages[existingIndex] = [
            existingQ,
            {
              ...existingMsg,
              messageId: response.messageId ?? existingMsg.messageId,
              assistant_files: response.assistant_files ?? existingMsg.assistant_files,
              user_files: response.user_files ?? existingMsg.user_files,
              interrupted: response.interrupted ?? existingMsg.interrupted,
              termination_reason: response.termination_reason ?? existingMsg.termination_reason,
              // Merge persisted context whenever we update this row (metadata, doc count, or same-count doc refresh).
              context: mergeSavedResponseContext(existingMsg.context, response.context),
            },
          ]
          store.updateSession(targetSessionId, {
            messages: updatedMessages,
            isAwaitingSave: false,
            sawMessageSaved: true,
          })
        }
      } else if (message.type === "title_state_changed") {
        const sessionId = message.data.session_id ?? effectiveSessionId
        if (!sessionId) return

        const update: Partial<ChatSessionModel> = {}
        if (message.data.title_updated?.new_title) {
          update.title = message.data.title_updated.new_title
          update.title_is_generic = message.data.title_is_generic ?? false
        }
        if (message.data.pending_title_suggestion) {
          update.pending_title_suggestion = message.data.pending_title_suggestion
        } else if ("pending_title_suggestion" in message.data) {
          update.pending_title_suggestion = undefined
        }

        const store = chatStore.getState()
        store.setSessions(
          store.sessions.map((s) => (s?.id === sessionId ? { ...s, ...update } : s))
        )
        queryClient.setQueriesData(
          { queryKey: ["session-history"] },
          (old: ChatSessionModel[] | undefined) =>
            old?.map((s) => (s?.id === sessionId ? { ...s, ...update } : s)) ?? old
        )

        // Automatic titling is a rename as far as the other windows are
        // concerned (issue #2548). Only on a real title change — a frame
        // that carries nothing but a pending suggestion leaves the list
        // rows unchanged, and this bus is for session-level changes, never
        // per-frame chatter.
        if (update.title) {
          publishSessionChange("renamed")
        }
      } else if (message.type === "followup_questions") {
        // Follow-up questions arrive after the main response to avoid blocking the stream
        const followupData = message.data
        if (followupData.followup_questions.length > 0) {
          const store = chatStore.getState()
          const sessionId = effectiveSessionId
          if (sessionId && sessionId in store.sessionStates) {
            const sessionState = store.sessionStates[sessionId]
            const msgs = [...sessionState.messages]
            const followupMessageId = followupData.message_id ?? followupData.messageId
            const targetIndex =
              followupMessageId != null
                ? msgs.findIndex(([, answer]) => getMessageId(answer) === followupMessageId)
                : msgs.length - 1
            if (targetIndex >= 0) {
              const [q, targetAnswer] = msgs[targetIndex]
              msgs[targetIndex] = [
                q,
                {
                  ...targetAnswer,
                  context: {
                    ...targetAnswer.context,
                    followup_questions: followupData.followup_questions,
                  },
                },
              ]
              store.updateSession(sessionId, { messages: msgs })
            }
          }
        }
      } else if (message.type === "done") {
        // Fair use (#2625): the gate appends `fair_use: { percent, status }`
        // to the final message payload — routed into the usage query cache
        // (so the composer indicator updates without polling) ABOVE, before
        // the disposed-session guard, since that data is per-user.
        if (effectiveSessionId) {
          const store = chatStore.getState()
          if (effectiveSessionId in store.sessionStates) {
            const sessionState = store.sessionStates[effectiveSessionId]
            // `done` only ends the spinners. It deliberately does NOT touch
            // isAwaitingSave/queueRequestId: a trailing `done` from a prior
            // connection is indistinguishable here from the active turn's, so
            // clearing the guard from `done` races with a freshly-started turn.
            // The composer guard is released by `message_saved` (normal case)
            // or, if that ack is skipped, by the save-guard watchdog (use-chat)
            // — the single race-free fallback (ServiceDesk 130507).
            if (sessionState.isStreaming || sessionState.isLoading) {
              store.updateSession(effectiveSessionId, {
                isStreaming: false,
                isLoading: false,
              })
            }
          }
        }

        // Session completed - invalidate sessions query to refresh list
        if (sessionType && effectiveSessionId) {
          const sessionHistoryQueryKey = queryKeys.sessionHistory(sessionType)
          void queryClient.invalidateQueries({
            queryKey: sessionHistoryQueryKey,
          })
          // Follow-up questions are persisted in the backend as a background task.
          // Revalidate once more shortly after completion to pick up that update.
          window.setTimeout(() => {
            void queryClient.invalidateQueries({
              queryKey: sessionHistoryQueryKey,
            })
          }, 2000)
        }
      } else if (message.type === "error") {
        // Surface transport or server errors to the UI and stop any spinners.
        const errorMsg = message as ErrorMessage
        const sessionId = effectiveSessionId
        const conversationForgotten =
          errorMsg.status === 410 && errorMsg.data?.code === "conversation_forgotten"

        // Fair use (#2625): a structured fair-use error refreshes the usage
        // cache + auto-opens the composer popover (request-more flow) —
        // routed into fairUseErrorCode ABOVE, before the disposed-session
        // guard, since that data is per-user. The in-chat notice below uses
        // the friendly localized copy instead of the raw backend message.
        // The two codes get DIFFERENT copy on purpose — `fair_use_deep_
        // unavailable` means a Deep Explorer run was refused before it
        // started because the user is near the cap, not that the budget is
        // finished, and ordinary chat still works.

        // Some errors (e.g. FileUploadError for an unreadable/encrypted
        // attachment) carry an i18n hint — prefer the localized string over
        // the raw (English) backend message when a translation exists.
        //
        // Deep Explorer frames carry their text and i18n hint under `data`
        // (`{type: "error", data: {message, translation_key, source}}`), so
        // read both places — otherwise every Deep stop reads as the generic
        // "An error occurred" (issue #3319).
        const rawMessage = errorMsg.message || errorMsg.data?.message
        const translationKey = errorMsg.translation_key ?? errorMsg.data?.translation_key
        const translationParams = errorMsg.translation_params ?? errorMsg.data?.translation_params
        let displayMessage: string
        if (conversationForgotten) {
          displayMessage = t("personalization.memoryPanel.prism.chatForgotten")
        } else if (fairUseErrorCode === "fair_use_blocked") {
          displayMessage = t("fairUse.chat.blockedNotice")
        } else if (fairUseErrorCode === "fair_use_deep_unavailable") {
          displayMessage = t("fairUse.chat.deepUnavailableNotice")
        } else if (translationKey) {
          displayMessage = t(translationKey, {
            ...translationParams,
            defaultValue: rawMessage || "An error occurred",
          })
        } else {
          displayMessage = rawMessage || "An error occurred"
        }

        // Update session state with error
        if (sessionId) {
          const store = chatStore.getState()
          if (!(sessionId in store.sessionStates)) {
            store.initSession(sessionId)
          }
          store.updateSession(sessionId, {
            error: displayMessage,
            // Companion of `error`: the session-error Chat.Error card has no
            // answer object to read `error_code` from. Stamp the structured
            // fair-use code (or clear a stale one) so `"fair_use_blocked"`
            // can select the budget gate instead of Retry/Report.
            errorCode: conversationForgotten
              ? "conversation_forgotten"
              : (fairUseErrorCode ?? undefined),
            ...(conversationForgotten ? { canContinue: false } : {}),
            isLoading: false,
            isStreaming: false,
            isAwaitingSave: false,
          })
        }

        // Only update global UI state if this is the active session
        if (sessionId === chatStore.getState().currentSessionId) {
          setIsLoading(false)
          setHasError(true)
          setErrorMessage(displayMessage)
        }
      }
    },
    [
      t,
      setActivityMessage,
      setIsLoading,
      setHasError,
      setErrorMessage,
      chatStore,
      queryClient,
      sessionType,
      isMessageQueueEnabled,
      publishSessionChange,
    ]
  )

  return messageHandler
}
